import { SHAutocomplete, SHStack } from "@components/design-systems";
import { forwardRef, useMemo, useState } from "react";
import { useTheme } from "@mui/material";
import {
  ModelScreenerFilterDropdownLov,
  ModelScreenerFilterDTO,
  ModelScreenerFilterItemDTO,
} from "@models/model-screener/entities/modelScreenerFilter";
import {
  EditMSFilterCombinedSliderFieldPath,
  getMSFilterRenderedItemInitialProps,
  getMSFilterSelectedLovValue,
  MSFilterCombinedSliderInitialProps,
  MSFilterRenderedItemProps,
  MSTableFilterItemRef,
} from "@pages/managed-accounts/model-screener/components/model-screener-table/toolbar/filter/utils";
import { MSFilterSlider } from "@pages/managed-accounts/model-screener/components/model-screener-table/toolbar/filter/filter-item/slider";
import { useMount, useToggle } from "react-use";
import { Controller, useFormContext } from "react-hook-form";
import { MSFilterItemLabel } from "@pages/managed-accounts/model-screener/components/model-screener-table/toolbar/filter/filter-item/label";
import { ModelScreenerFilterItemType } from "@models/model-screener/enums/filterType";
import { isEqual, isNil } from "lodash";
import { MSFilterCombinedSliderSkeleton } from "../../skeletons/combined-slider";

export const MSFilterCombinedSlider = forwardRef<
  MSTableFilterItemRef,
  MSFilterRenderedItemProps
>(({ item, itemSize, itemPaths, disabled }: MSFilterRenderedItemProps, ref) => {
  const { palette, shadows } = useTheme();
  const combinedSliderFieldPath =
    itemPaths as EditMSFilterCombinedSliderFieldPath;
  const { selectDropdownLabel, defaultSelectOptions, sliderItemProps } =
    getMSFilterRenderedItemInitialProps<MSFilterCombinedSliderInitialProps>(
      item,
    ) || {};

  const { control, getValues } = useFormContext<ModelScreenerFilterDTO>();

  const initialSelectOptions = useMemo(() => {
    return defaultSelectOptions || [];
  }, [defaultSelectOptions]);

  const [isChanging, toggleIsChanging] = useToggle(false);
  const [isMounted, toggleMounted] = useToggle(false);
  const [currentSelectValue, setCurrentSelectValue] = useState<
    ModelScreenerFilterDropdownLov[]
  >([]);

  useMount(() => {
    const formSelectValue = getValues(combinedSliderFieldPath.lov);
    setCurrentSelectValue(
      formSelectValue
        ? (formSelectValue as ModelScreenerFilterDropdownLov[])
        : [],
    );

    toggleMounted(true);
  });

  const handleOnSelectDropdownChange = (
    value: ModelScreenerFilterDropdownLov | null,
  ): ModelScreenerFilterDropdownLov[] => {
    if (isNil(value)) {
      setCurrentSelectValue(initialSelectOptions);
      return initialSelectOptions;
    }

    const newValue = getMSFilterSelectedLovValue(currentSelectValue, value);
    setCurrentSelectValue(newValue);
    return newValue;
  };

  const selectedValue: ModelScreenerFilterDropdownLov | undefined =
    useMemo(() => {
      return currentSelectValue?.find((v) => v.isSelected);
    }, [currentSelectValue]);

  const selectedItems = useMemo(() => {
    return sliderItemProps?.[selectedValue?.value ?? ""];
  }, [selectedValue?.value, sliderItemProps]);

  return (
    <SHStack gap="16px" ref={ref}>
      <SHStack
        padding="10px"
        borderRadius="12px"
        bgcolor={palette.common?.white}
        boxShadow={shadows[1]}
        alignItems={"start"}
      >
        <MSFilterItemLabel size={itemSize} disabled={disabled}>
          {selectDropdownLabel}
        </MSFilterItemLabel>

        <SHStack key={`${item.id}-${isMounted}`} width={"100%"}>
          <Controller
            control={control}
            name={combinedSliderFieldPath.lov}
            render={({ field: { ref, ...other } }) => (
              <SHAutocomplete
                disabled={disabled}
                value={selectedValue}
                options={initialSelectOptions}
                onChange={(_, lovItem) => {
                  const newValue = handleOnSelectDropdownChange(lovItem);
                  const isEqualValue = isEqual(newValue, currentSelectValue);
                  if (isEqualValue) return;
                  !isNil(lovItem) && toggleIsChanging(true);

                  other.onChange(newValue);
                  !isNil(lovItem) &&
                    setTimeout(() => toggleIsChanging(false), 300);
                }}
                getOptionLabel={(option) => option.name ?? ""}
                textFieldProps={{
                  placeholder: "Choose period",
                }}
              />
            )}
          />
        </SHStack>
      </SHStack>

      <SHStack gap={1.5} display={!isChanging ? "flex" : "none"}>
        {selectedItems &&
          selectedItems?.map((itemProp, index) => {
            const pseudoItemDto: ModelScreenerFilterItemDTO = {
              id: `${item.id}-${index}`,
              filterType: ModelScreenerFilterItemType.Range,
              data: itemProp,
            };
            const pseudoItemPaths =
              combinedSliderFieldPath?.sliderPaths?.[
                selectedValue?.value ?? ""
              ]?.[index];

            return (
              <MSFilterSlider
                key={`${selectedValue?.value}-${index}`}
                item={pseudoItemDto}
                itemPaths={pseudoItemPaths}
                itemSize={itemSize}
                disabled={disabled}
                allowOverflow
              />
            );
          })}
      </SHStack>
      <SHStack display={isChanging ? "flex" : "none"}>
        <MSFilterCombinedSliderSkeleton />
      </SHStack>
      {/*Slider items*/}
    </SHStack>
  );
});
